import { createAppError } from '../../api/errors';
import type { ApiDownload } from '../../api/http';
import type { ReportExportsRepository } from './repository';
import type {
  CreateReportExportInput,
  ReportExport,
  ReportExportDetail,
  ReportExportListQuery,
  ReportExportStatus,
} from './types';

export interface MockReportExportsRepositoryOptions {
  readonly status?: ReportExportStatus;
}

export interface MockReportExportCreation {
  readonly input: CreateReportExportInput;
  readonly idempotencyKey: string;
}

export class MockReportExportsRepository implements ReportExportsRepository {
  readonly creations: MockReportExportCreation[] = [];
  readonly queries: ReportExportListQuery[] = [];
  readonly #job: ReportExportDetail;

  constructor(options: MockReportExportsRepositoryOptions = {}) {
    this.#job = {
      ...mockReportExport,
      status: options.status ?? mockReportExport.status,
      requestId: 'mock-report-export-detail',
    };
  }

  list(query: ReportExportListQuery, signal: AbortSignal) {
    assertActive(signal);
    this.queries.push({ ...query, filters: { ...query.filters } });
    return Promise.resolve({
      items: [this.#job],
      nextCursor: null,
      requestId: 'mock-report-export-list',
    });
  }

  create(input: CreateReportExportInput, idempotencyKey: string) {
    this.creations.push({ input: { ...input, filters: { ...input.filters } }, idempotencyKey });
    return Promise.resolve({ data: this.#job, requestId: 'mock-report-export-create' });
  }

  get(_exportId: string, signal: AbortSignal) {
    assertActive(signal);
    return Promise.resolve(this.#job);
  }

  download(_exportId: string, signal: AbortSignal): Promise<ApiDownload> {
    assertActive(signal);
    if (this.#job.status !== 'COMPLETED') {
      throw createAppError({
        status: 409,
        code: 'REPORT_EXPORT_NOT_READY',
        message: 'The report export is not ready',
        requestId: 'mock-report-export-download',
        fieldErrors: {},
        retryAfterSeconds: null,
      });
    }
    return Promise.resolve({
      blob: new Blob(['id,amount\r\n1,12345.678901\r\n'], { type: 'text/csv' }),
      filename: 'affiliate-report.csv',
      requestId: 'mock-report-export-download',
    });
  }
}

export const mockReportExport: ReportExport = Object.freeze({
  id: '00000000-0000-4000-8000-000000000101',
  requestedBy: '00000000-0000-4000-8000-000000000102',
  partnerId: null,
  agentId: null,
  reportType: 'TRANSACTIONS',
  format: 'CSV',
  filters: { timezone: 'Asia/Bangkok', currency: 'THB' },
  timezone: 'Asia/Bangkok',
  currencyCode: 'THB',
  status: 'COMPLETED',
  snapshotId: '00000000-0000-4000-8000-000000000103',
  recordCount: 42,
  contentLength: 1024,
  generatedAt: '2026-07-23T10:00:00.123456+07:00',
  completedAt: '2026-07-23T10:00:01.123456+07:00',
  expiresAt: '2099-07-24T10:00:01.123456+07:00',
  failureCode: null,
  createdAt: '2026-07-23T09:59:59.123456+07:00',
  updatedAt: '2026-07-23T10:00:01.123456+07:00',
});

function assertActive(signal: AbortSignal): void {
  if (signal.aborted) throw new DOMException('Report export request aborted', 'AbortError');
}
